// Detect the user's country to choose the primary CTA store.
//
// Order of precedence:
//   1. ?geo=US|UK|IN  query override (for testing + ad-specific landing URLs)
//   2. CF-IPCountry — populated by Cloudflare on every response served from
//      the Pages edge. We surface it via the /api/geo Pages Function.
//   3. Browser language fallback (en-IN → IN, en-GB → UK, etc.)
//   4. Default 'US' (largest English market)

export async function detectCountry() {
  if (typeof window === 'undefined') return 'US';

  // 1. URL override
  const params = new URLSearchParams(window.location.search);
  const override = (params.get('geo') || '').toUpperCase();
  if (/^[A-Z]{2}$/.test(override)) return override;

  // 2. CF edge header via /api/geo Function
  try {
    const res = await fetch('/api/geo', { cache: 'no-store' });
    if (res.ok) {
      const j = await res.json();
      if (j.country) return j.country.toUpperCase();
    }
  } catch {}

  // 3. Browser language
  const lang = (navigator.language || '').toLowerCase();
  if (lang.endsWith('-in')) return 'IN';
  if (lang.endsWith('-gb') || lang.endsWith('-uk')) return 'UK';
  if (lang.endsWith('-au')) return 'AU';
  if (lang.endsWith('-ca')) return 'CA';

  // 4. Default
  return 'US';
}

// Pick the primary store + label for a given book + country.
// Falls back gracefully when a book doesn't have a listing in the requested store.
export function selectPrimaryCTA(book, country) {
  const ids = book?.platform_ids || {};
  // Only flag KU when explicitly set in the DB. The presence of a KDP ASIN
  // alone doesn't mean the book is enrolled in Kindle Unlimited.
  const isKU = !!book?.is_kindle_unlimited;

  // India → Google Play preferred (closed-loop attribution + no KU friction)
  if (country === 'IN' && (ids.google_play_url || ids.google_books_id)) {
    return {
      store: 'google_play',
      market: 'IN',
      url: ids.google_play_url ||
        `https://play.google.com/store/books/details?id=${ids.google_books_id}`,
      label: 'Read on Google Play Books',
    };
  }
  // US → Kindle US
  if (country === 'US' && (ids.amazon_us || ids.amazon_us_kindle)) {
    return {
      store: 'amazon_us',
      market: 'US',
      url: ids.amazon_us || ids.amazon_us_kindle,
      label: isKU ? 'Read free on Kindle Unlimited' : 'Read on Kindle',
    };
  }
  // UK → Kindle UK (when listing exists)
  if (country === 'UK' && ids.amazon_uk) {
    return { store: 'amazon_uk', market: 'UK', url: ids.amazon_uk, label: 'Read on Kindle UK' };
  }
  // IN fallback → Kindle India
  if (country === 'IN' && (ids.amazon_in || ids.amazon_in_kindle)) {
    return {
      store: 'amazon_in',
      market: 'IN',
      url: ids.amazon_in || ids.amazon_in_kindle,
      label: 'Read on Kindle India',
    };
  }
  // Default fallback: any Amazon US, Google Play, then Amazon IN
  if (ids.amazon_us || ids.amazon_us_kindle) {
    return {
      store: 'amazon_us',
      market: 'US',
      url: ids.amazon_us || ids.amazon_us_kindle,
      label: 'Read on Kindle',
    };
  }
  if (ids.google_play_url || ids.google_books_id) {
    return {
      store: 'google_play',
      market: 'US',
      url: ids.google_play_url ||
        `https://play.google.com/store/books/details?id=${ids.google_books_id}`,
      label: 'Read on Google Play Books',
    };
  }
  if (ids.amazon_in || ids.amazon_in_kindle) {
    return {
      store: 'amazon_in',
      market: 'IN',
      url: ids.amazon_in || ids.amazon_in_kindle,
      label: 'Read on Kindle India',
    };
  }
  return null;
}
